import api from './api';
import { authStore } from './authStore';

const API_URL = '/transporteur/missions';
const EXPEDITEUR_API_URL = '/expediteur/missions';

// Get all missions for the current user
export const getMissions = async (filters = {}) => {
  try {
    // Use the appropriate endpoint based on user type from authStore
    const userType = authStore.getState().user?.userType;
    const endpoint = userType === 'expediteur'
      ? EXPEDITEUR_API_URL
      : API_URL;

    const response = await api.get(endpoint, {
      params: filters,
      // Add timeout to prevent hanging requests
      timeout: 10000
    });
    return response.data;
  } catch (error) {
    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Please log in to view missions');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to view missions');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. Please try again.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch missions');
    }
  }
};

// Get a single mission by ID
export const getMission = async (id) => {
  try {
    const userType = authStore.getState().user?.userType;
    const base = userType === 'expediteur' ? EXPEDITEUR_API_URL : API_URL;
    const endpoint = `${base}/${id}`;
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.status === 403) {
        throw new Error('You do not have permission to access this mission');
      }
      if (error.response.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      }
    }
    throw new Error(error.response?.data?.message || 'Failed to fetch mission');
  }
};

// Create a new mission
export const createMission = async (missionData) => {
  try {
    // Use the appropriate endpoint based on user type
    const userType = authStore.getState().user?.userType;
    const endpoint = userType === 'expediteur' ? EXPEDITEUR_API_URL : API_URL;

    // Validate required fields before sending
    if (!missionData.departureLocation || !missionData.arrivalLocation ||
      !missionData.departureDate || !missionData.arrivalDate ||
      !missionData.cargoType || !missionData.vehicleType ||
      !missionData.weight || !missionData.length) {
      throw new Error('Please fill in all required fields');
    }

    const response = await api.post(endpoint, missionData);
    return response.data;
  } catch (error) {
    // Handle specific error cases
    if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid mission data');
    } else if (error.response?.status === 409) {
      throw new Error('A mission with these details already exists');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to create missions');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to create missions');
    } else if (error.response?.data?.message?.includes('No expediteur associated')) {
      throw new Error('You need to be associated with an expediteur to create missions. Please contact your administrator.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to create mission. Please try again.');
    }
  }
};

// Update mission information
export const updateMission = async (id, missionData) => {
  try {
    const authState = authStore.getState();
    const userType = authState.user?.userType;

    if (!authState.isAuthenticated) {
      throw new Error('No authentication token found. Please log in.');
    }

    if (!userType) {
      throw new Error('User type not found. Please log in again.');
    }

    // Use the appropriate endpoint based on user type
    const endpoint = userType === 'expediteur' ? EXPEDITEUR_API_URL : API_URL;

    // Ensure id is treated as a string
    const missionId = String(id);

    const response = await api.put(`${endpoint}/${missionId}`, {
      ...missionData,
      id: missionId // Ensure ID is included in the request body
    });

    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Mission not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid mission data');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to update this mission');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to update missions');
    } else if (error.code === 'ECONNABORTED') {
      throw new Error('Request timed out. Please try again.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to update mission. Please try again.');
    }
  }
};

// Delete a mission
export const deleteMission = async (id) => {
  try {
    // Use the appropriate endpoint based on user type
    const userType = authStore.getState().user?.userType;
    const endpoint = userType === 'expediteur' ? EXPEDITEUR_API_URL : API_URL;

    const response = await api.delete(`${endpoint}/${id}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Mission not found');
    } else if (error.response?.status === 400 && error.response.headers['x-error-message']) {
      // Propager l'erreur spécifique pour gestion côté React
      const err = new Error(error.response.headers['x-error-message']);
      err.isMissionInProgress = true;
      throw err;
    } else {
      throw new Error(error.response?.data?.message || 'Failed to delete mission');
    }
  }
};

// Assign a chauffeur to a mission
export const assignChauffeur = async (missionId, chauffeurId) => {
  try {
    const response = await api.post(`${API_URL}/${missionId}/assign`, { chauffeurId });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Mission or chauffeur not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid assignment data');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to assign chauffeur');
    }
  }
};

// Update mission status
export const updateMissionStatus = async (missionId, status) => {
  try {
    const response = await api.put(`${API_URL}/${missionId}/status`, { status });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Mission not found');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data.message || 'Invalid status');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to update mission status');
    }
  }
};

export const revertMissionToPending = async (missionId) => {
  try {
    const response = await api.post(`/transporteur/missions/${missionId}/revert-to-pending`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to revert mission to pending');
  }
};

// Get available chauffeurs for mission assignment
export const getAvailableChauffeurs = async (transporteurId) => {
  try {
    const response = await api.get(`${API_URL}/available-chauffeurs`, {
      params: { transporteurId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch available chauffeurs');
  }
};

// Get available vehicles for mission assignment
export const getAvailableVehicles = async (transporteurId) => {
  try {
    const response = await api.get('/transporteur/missions/available-vehicles', {
      params: { transporteurId }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get available remorques for mission assignment
export const getAvailableRemorques = async (transporteurId) => {
  try {
    const response = await api.get('/transporteur/missions/available-remorques', {
      params: { transporteurId }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Assign a chauffeur, vehicle and trailer to a mission
export const assignMissionResources = async (missionId, chauffeurId, vehiculeId, remorqueId) => {
  try {
    const response = await api.post(
      `/transporteur/missions/assignments/${missionId}`,
      { chauffeurId, vehiculeId, remorqueId }  // Send in request body including remorqueId
    );
    return response.data;
  } catch (error) {
    // Handle specific error cases
    if (error.response) {
      const errorData = error.response.data;
      const errorMessage = errorData?.error ||
        errorData?.message ||
        errorData?.details ||
        'An unexpected error occurred';

      switch (error.response.status) {
        case 500:
          throw new Error(`Server error: ${errorMessage}`);
        case 403:
          throw new Error(errorMessage || 'You do not have permission to assign resources to this mission');
        case 400:
          throw new Error(errorMessage || 'Invalid request parameters');
        case 404:
          throw new Error(errorMessage || 'Mission, chauffeur, or vehicle not found');
        case 401:
          throw new Error('Your session has expired. Please log in again.');
        default:
          throw new Error(errorMessage || 'Failed to assign resources. Please try again.');
      }
    }
    throw new Error('Network error. Please check your connection and try again.');
  }
};

export const getMissionAssignment = async (missionId) => {
  try {
    const response = await api.get(`/transporteur/missions/assignments/${missionId}`);
    return response.data;
  } catch (error) {
    // Don't throw error for 404 as it just means no assignment exists yet
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getMissionAssignmentHistory = async (missionId) => {
  try {
    const response = await api.get(`/transporteur/missions/assignments/${missionId}/history`);
    return response.data;
  } catch (error) {
    // 404 means no history exists
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getMissionMobileData = async (missionId) => {
  try {
    const response = await api.get(`/transporteur/missions/${missionId}/mobile-data`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.response?.data?.message || 'Failed to fetch mission mobile data');
  }
};

export const analyzeMission = async (missionId) => {
  try {
    const response = await api.post(`/agent/planning/missions/${missionId}/analyze`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "L'agent IA est indisponible.");
  }
};

export const triggerAISuggestion = async (missionId) => {
  try {
    const response = await api.post(`/agent/planning/missions/${missionId}/suggest`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Erreur lors de la demande de suggestion.");
  }
};

export const getAIProposals = async (missionId) => {
  try {
    const response = await api.get(`/agent/planning/missions/${missionId}/proposals`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Erreur lors de la récupération des propositions.");
  }
};

export const decideAIProposal = async (proposalId, action) => {
  try {
    const response = await api.post(`/agent/planning/proposals/${proposalId}/decide?action=${action}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || `Erreur lors de la décision (${action}).`);
  }
};

export const getChauffeurAssignments = async (chauffeurId) => {
  try {
    const response = await api.get(`/transporteur/missions/assignments/chauffeur/${chauffeurId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getVehiculeAssignments = async (vehiculeId) => {
  try {
    const response = await api.get(`/transporteur/missions/assignments/vehicule/${vehiculeId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Single-call check: returns true if the chauffeur is currently active
 * (EN_COURS or EN_PAUSE) on any mission other than excludeMissionId.
 * The check is done in a single DB query on the backend — no N+1.
 */
export const isChauffeurActiveOnAnotherMission = async (chauffeurId, excludeMissionId) => {
  try {
    const response = await api.get(
      `/transporteur/missions/assignments/chauffeur/${chauffeurId}/active-check`,
      { params: { excludeMissionId } }
    );
    return response.data.busy === true;
  } catch (error) {
    throw error;
  }
};

export const removeMissionAssignment = async (missionId) => {
  try {
    const response = await api.delete(`/transporteur/missions/assignments/${missionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get all assigned missions
export const getAssignedMissions = async (transporteurId) => {
  try {
    const response = await api.get('/transporteur/missions/assigned', {
      params: { transporteurId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch assigned missions');
  }
};

// =========================
// Expediteur-specific APIs
// =========================

export const getAvailableTransporteursForExpediteur = async () => {
  try {
    const response = await api.get('/expediteur/transporteurs');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch transporteurs');
  }
};

export const createTransporteurForExpediteur = async (payload) => {
  try {
    const response = await api.post('/expediteur/transporteurs', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create transporteur');
  }
};

export const selectTransporteurForMission = async (missionId, transporteurId) => {
  try {
    // Create an offer for the transporteur; backend will keep mission EN_ATTENTE
    const response = await api.post(`/expediteur/missions/${missionId}/transporteur/${transporteurId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('Mission or transporteur not found');
    }
    throw new Error(error.response?.data?.message || 'Failed to send mission to transporteur');
  }
};

export const getDeliveryProofForMission = async (missionId) => {
  try {
    const response = await api.get(`/expediteur/missions/${missionId}/delivery-proof`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return null;
    }
    throw new Error(error.response?.data?.message || 'Failed to fetch delivery proof');
  }
};

export const getContractForMission = async (missionId, transporteurId) => {
  if (!missionId || !transporteurId) {
    throw new Error('Mission and transporteur are required');
  }
  try {
    const response = await api.get(`/expediteur/missions/${missionId}/contract`, {
      params: { transporteurId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch contract');
  }
};

export const sendContractForMission = async (missionId, transporteurId, currency) => {
  if (!missionId || !transporteurId) {
    throw new Error('Mission and transporteur are required');
  }
  try {
    const response = await api.post(
      `/expediteur/missions/${missionId}/contract/send`,
      {},
      { params: { transporteurId, currency } }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send contract');
  }
};

export const getMissionDocumentsForExpediteur = async (missionId) => {
  try {
    const response = await api.get(`/expediteur/missions/${missionId}/documents`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch mission documents');
  }
};

// Upload a document for a mission (Expediteur)
export const uploadMissionDocumentForExpediteur = async (missionId, file, metadata = {}) => {
  try {
    if (!file) {
      throw new Error('No file selected');
    }
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(metadata || {}).forEach(([key, value]) => {
      if (value != null) formData.append(key, String(value));
    });

    const response = await api.post(`/expediteur/missions/${missionId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to upload document');
  }
};

// =========================
// Subcontracting APIs
// =========================

export const searchSubcontractors = async (query) => {
  try {
    const response = await api.get('/transporteur/subcontract/search', { params: { query } });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to search transporters');
  }
};

export const createSubcontractor = async (payload) => {
  try {
    const response = await api.post('/transporteur/subcontract/create', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create subcontractor');
  }
};

export const assignSubcontractor = async (payload) => {
  try {
    const response = await api.post('/transporteur/subcontract/assign', payload);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to assign subcontractor');
  }
};

export const getSubcontractPreview = async (missionId, subcontractorId, currency = 'EUR') => {
  try {
    const response = await api.get('/transporteur/subcontract/preview', {
      params: { missionId, subcontractorId, currency }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to preview contract');
  }
};

export const sendSubcontractContract = async (missionId, subcontractorId, currency = 'EUR') => {
  try {
    const response = await api.post('/transporteur/subcontract/send-contract', null, {
      params: { missionId, subcontractorId, currency }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send contract');
  }
};

export const getMySubcontractors = async () => {
  try {
    const response = await api.get('/transporteur/subcontract/list');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontractors');
  }
};

export const addSubcontractor = async (subcontractorId) => {
  try {
    const response = await api.post('/transporteur/subcontract/add-collaborator', null, {
      params: { subcontractorId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to add collaborator');
  }
};

export const getSubcontractedMissions = async () => {
  try {
    const response = await api.get('/transporteur/subcontract/missions');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontracted missions');
  }
};
export const getSubcontractByMissionId = async (missionId) => {
  try {
    const response = await api.get(`/transporteur/subcontract/mission/${missionId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontract details');
  }
};

export const uploadMissionDocument = async (missionId, file, metadata = {}) => {
  try {
    if (!file) throw new Error('No file selected');
    const formData = new FormData();
    formData.append('file', file);
    if (metadata.name) formData.append('name', metadata.name);
    if (metadata.type) formData.append('type', metadata.type);
    const response = await api.post(`/transporteur/missions/${missionId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to upload document');
  }
};

export const downloadSubcontractContract = async (missionId) => {
  try {
    const response = await api.get(`/transporteur/missions/${missionId}/subcontract-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to download subcontract contract');
  }
};

// =========================
// Subcontractor Invitation APIs
// =========================

export const sendSubcontractorInvitation = async (recipientId, message = null) => {
  try {
    const response = await api.post('/transporteur/subcontract/invitations/send', null, {
      params: { recipientId, message }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to send invitation');
  }
};

export const getReceivedInvitations = async () => {
  try {
    const response = await api.get('/transporteur/subcontract/invitations/received');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch received invitations');
  }
};

export const getSentInvitations = async () => {
  try {
    const response = await api.get('/transporteur/subcontract/invitations/sent');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch sent invitations');
  }
};

export const acceptInvitation = async (invitationId) => {
  try {
    const response = await api.post(`/transporteur/subcontract/invitations/${invitationId}/accept`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to accept invitation');
  }
};

export const rejectInvitation = async (invitationId) => {
  try {
    const response = await api.post(`/transporteur/subcontract/invitations/${invitationId}/reject`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to reject invitation');
  }
};

// =========================
// Subcontractor Driver Management
// =========================

export const getSubcontractorDetails = async (id) => {
  try {
    const response = await api.get(`/transporteur/subcontract/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontractor details');
  }
};

export const getSubcontractorDrivers = async (id) => {
  try {
    const response = await api.get(`/transporteur/subcontract/${id}/drivers`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontractor drivers');
  }
};

export const createSubcontractorDriver = async (id, driverData) => {
  try {
    const response = await api.post(`/transporteur/subcontract/${id}/drivers`, driverData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to create driver');
  }
};

export const getMissionsSubcontractedToPartner = async (subcontractorId) => {
  try {
    const response = await api.get(`/transporteur/subcontract/${subcontractorId}/missions`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch subcontracted missions');
  }
};
